import React from 'react';
import {
  Link,
  DateRangeFilter,
  ExternalLink,
  Gap,
  Gutter,
  Text,
  TextFilterSearch,
  TextOverflow,
  WorksAvatar,
} from '@common/components';

import { RolesQuery } from '@clinician-profile/graphql';
import {
  OrderDirection,
  StaffingRequestAssignmentOrderField,
} from '@common/graphql';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import {
  ORGANIZATION_BUSINESSES_QUERY,
  WORKS_CLINICAL_UNITS_QUERY,
} from '@institutions/graphql';
import { ComplianceActionsDropdown } from '@onboarding/components';
import {
  MISSION_CONTROL_COMPLIANCE_ASSIGNMENTS_QUERY,
  MISSION_CONTROL_COMPLIANCE_FILTER_SUPPLIERS_QUERY,
  MissionControlComplianceAssignment,
  MissionControlComplianceStatusType,
} from '@working-experience/graphql/queries';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { RenderTableColumn } from 'works/components/filtered-table/render-filtered-table';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';

const LOCAL_STORAGE_KEY = 'MissionControlComplianceTable';

type MissionControlComplianceTablePropsType = {
  complianceStatus: MissionControlComplianceStatusType;
};

export function MissionControlComplianceTable({
  complianceStatus,
}: MissionControlComplianceTablePropsType) {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: `${LOCAL_STORAGE_KEY}_${complianceStatus}`,
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'START',
    },
    columnsDictionary: {
      name: 'NAME',
      supplier: 'SUPPLIER_NAME',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      clinicalUnit: 'CLINICAL_UNIT',
      role: 'ROLE',
      startDate: 'START',
      hiringLeader: 'HIRING_LEADER',
    },
  });

  const [fetchOrgBusiness, { data: orgBusinessData }] = useLazyQuery(
    ORGANIZATION_BUSINESSES_QUERY
  );

  const [fetchSupplier, { data: supplierData }] = useLazyQuery(
    MISSION_CONTROL_COMPLIANCE_FILTER_SUPPLIERS_QUERY
  );

  const [fetchRoles, { data: rolesData }] = useLazyQuery(RolesQuery);

  const [fetchClinicalUnits, { data: clinicalUnitData }] = useLazyQuery(
    WORKS_CLINICAL_UNITS_QUERY
  );

  const { data, loading } = useQuery(
    MISSION_CONTROL_COMPLIANCE_ASSIGNMENTS_QUERY,
    {
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderField: paginationVariables.orderField as
          | StaffingRequestAssignmentOrderField
          | undefined,
        orderDirection: paginationVariables.orderDirection as
          | OrderDirection
          | undefined,
        filters: {
          ...paginationVariables.filters,
          complianceStatus,
        },
      },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchOrgBusiness();
        fetchSupplier();
        fetchRoles();
        fetchClinicalUnits();
      },
    }
  );

  const assignments =
    data?.missionControlPaginatedAssignments.staffingRequestAssignments || [];
  const totalCount = data?.missionControlPaginatedAssignments.totalCount || 0;

  const filtersDataReady =
    !!orgBusinessData && !!supplierData && !!rolesData && !!clinicalUnitData;

  const columns: RenderTableColumn<MissionControlComplianceAssignment>[] = [
    {
      title: 'Name',
      key: 'name',
      width: 200,
      sorter: true,
      filtered: !!filters.name,
      filteredValue: filters.name || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
      render: (_, assignment: MissionControlComplianceAssignment) => {
        const name = assignment.worksProfile.fullName;
        const namePieces = name.split(' ');
        const firstName = namePieces[0];
        const lastName = namePieces[namePieces.length - 1];

        return (
          <Link
            to={`/works/team/long_term/${assignment.id}/compliance`}
            target="_blank"
          >
            <Gap size="sm" align="center">
              <WorksAvatar
                firstName={firstName}
                lastName={lastName}
                size={32}
              />
              <Text bold color="elm">
                {name}
              </Text>
            </Gap>
          </Link>
        );
      },
    },
    {
      title: 'Supplier',
      dataIndex: ['supplier', 'name'],
      key: 'supplier',
      width: 140,
      sorter: true,
      filtered: !!filters.supplier,
      filteredValue: filters.supplier || null,
      filterDropdown: MultiSelectTableFilter,
      filters: supplierData && buildOptionsFromData(supplierData.suppliers),
    },
    {
      title: 'Facility',
      dataIndex: ['organizationBusiness', 'name'],
      key: 'organizationBusiness',
      width: 120,
      sorter: true,
      filtered: !!filters.organizationBusiness,
      filteredValue: filters.organizationBusiness || null,
      filters: buildOptionsFromData(
        orgBusinessData?.organizationBusinesses || [],
        (organizationBusiness: { id: string; name: string }) => ({
          value: organizationBusiness.id,
          text: organizationBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Unit',
      dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
      key: 'clinicalUnit',
      width: 180,
      sorter: true,
      filtered: !!filters.clinicalUnit,
      filteredValue: filters.clinicalUnit || null,
      filters:
        clinicalUnitData &&
        buildOptionsFromData(
          clinicalUnitData.worksClinicalUnits,
          (worksClinicalUnit: { id: string; displayName: string }) => ({
            value: worksClinicalUnit.id,
            text: worksClinicalUnit.displayName,
          })
        ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Role',
      dataIndex: ['staffingRequest', 'role', 'abbreviation'],
      key: 'role',
      width: 100,
      sorter: true,
      filtered: !!filters.role,
      filteredValue: filters.role || null,
      filters:
        rolesData?.roles &&
        buildOptionsFromData(
          rolesData.roles,
          (role: { id: string; abbreviation: string }) => ({
            text: role.abbreviation,
            value: role.id,
          })
        ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 90,
      sorter: true,
      filterFormat: 'dateRange',
      filtered: !!filters.startDate,
      filteredValue: filters.startDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value ? setSelectedKeys(value as any) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
      render: (startDate: string) => {
        return startDate && toUSCompactDateFormat(startDate);
      },
    },
    {
      title: 'Hiring Manager',
      dataIndex: ['hiringLeader', 'name'],
      key: 'hiringLeader',
      width: 130,
      sorter: true,
      filtered: !!filters.hiringLeader,
      filteredValue: filters.hiringLeader || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal size="md">
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </Gutter>
      ),
    },
    {
      title: 'Missing/Total',
      dataIndex: 'id',
      key: 'missingTotal',
      width: 100,
      render: (_, assignment: MissionControlComplianceAssignment) => {
        return `${assignment.worksList.missingComplianceCount}/${assignment.worksList.totalItemsCount}`;
      },
    },
    {
      title: 'Critical Missing',
      key: 'criticalMissing',
      width: 200,
      render: (_, assignment: MissionControlComplianceAssignment) => (
        <div style={{ maxWidth: 200 }}>
          <TextOverflow tooltip>
            {assignment.worksList.missingItemsNames?.join(' | ') || 'N/A'}
          </TextOverflow>
        </div>
      ),
    },
    {
      title: 'Action',
      render: (_: unknown, assignment: MissionControlComplianceAssignment) => (
        <ComplianceActionsDropdown assignment={assignment} />
      ),
    },
  ];

  return (
    <div>
      <FilteredTable
        rowKey="key"
        columns={columns}
        dataSource={assignments}
        pagination={{ ...paginationVariables, total: totalCount }}
        loading={loading}
        filtersDataReady={filtersDataReady}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={filters}
        localStorageFilterKey={localStorageFilterKey}
      />
      <Gutter size="sm" direction="vertical">
        Showing {Math.min(totalCount, paginationVariables.limit || 10)}.{' '}
        <ExternalLink url="/works/compliance">View All</ExternalLink>.
      </Gutter>
    </div>
  );
}
